import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ENTITY_QUERY_KEYS } from 'app/modules/entities/queries/keys';
import { GetEntitiesPayload } from 'app/modules/entities/requests';
import { ShortEntityResponse } from 'app/modules/entities/responses';
import { useGetEntities } from 'app/modules/entities/queries/useGetEntities';
import { selectHasReadEntitiesPermissions } from 'app/modules/session/selectors';
import {
  EntitySearchV2Response,
  EntitySource,
} from 'app/modules/search/response';
import { ValueField, getDisplayName } from 'app/modules/search/helpers';
import { DEFAULT_OFFSET } from 'app/shared/pagination/constants';

const getEntitySources = (data: EntitySearchV2Response): EntitySource[] =>
  data.hits.map((hit) => hit.source);

interface UseEntitiesForFilterDisplayParams {
  ids: string[];
  valueField: ValueField;
}

/**
 * Hook that looks up entities for filter display.
 * First checks opensearch cache, then falls back to useGetEntities for missing entities.
 */
export const useEntitiesForFilterDisplay = ({
  ids,
  valueField,
}: UseEntitiesForFilterDisplayParams) => {
  const queryClient = useQueryClient();
  const hasReadEntitiesPermissions = useSelector(
    selectHasReadEntitiesPermissions,
  );

  // Get cached query data (recalculated on every render for reactivity)
  const cachedQueryData = queryClient.getQueriesData<EntitySearchV2Response>({
    queryKey: ENTITY_QUERY_KEYS.getEntitySearch(),
  });

  // Build lookup maps from opensearch cache
  const searchCacheMap = useMemo(() => {
    const externalIdMap = new Map<string, EntitySource>();
    const idMap = new Map<string, EntitySource>();
    const unit21IdMap = new Map<string, EntitySource>();

    cachedQueryData.forEach(([, queryData]) => {
      if (queryData) {
        const entities = getEntitySources(queryData);
        entities.forEach((entity) => {
          if (entity.external_id) {
            externalIdMap.set(entity.external_id, entity);
          }
          if (entity.id) {
            idMap.set(entity.id, entity);
          }
          if (entity.unit21_id) {
            unit21IdMap.set(String(entity.unit21_id), entity);
          }
        });
      }
    });

    return { externalIdMap, idMap, unit21IdMap };
  }, [cachedQueryData]);

  // Find entities in opensearch cache
  const cachedEntities = useMemo(() => {
    const found: Map<string, EntitySource> = new Map();

    ids.forEach((id) => {
      let entity: EntitySource | undefined;

      if (valueField === ValueField.EXTERNAL_ID) {
        entity = searchCacheMap.externalIdMap.get(id);
      } else {
        // valueField === ValueField.ID
        entity = searchCacheMap.idMap.get(id);
        if (!entity) {
          entity = searchCacheMap.unit21IdMap.get(id);
        }
      }

      if (entity) {
        found.set(id, entity);
      }
    });

    return found;
  }, [ids, valueField, searchCacheMap]);

  // Find missing entities that need to be fetched via useGetEntities
  const missingIds = useMemo(() => {
    return ids.filter((id) => !cachedEntities.has(id));
  }, [ids, cachedEntities]);

  // Derive payload from missingIds
  const payload = useMemo<GetEntitiesPayload>(() => {
    if (missingIds.length > 0) {
      return {
        offset: DEFAULT_OFFSET,
        limit: missingIds.length,
        entity_ids: valueField === ValueField.ID ? missingIds : undefined,
        entity_external_ids:
          valueField === ValueField.EXTERNAL_ID ? missingIds : undefined,
      };
    }
    return {
      offset: DEFAULT_OFFSET,
      limit: 0,
    };
  }, [missingIds, valueField]);

  // Fetch missing entities via useGetEntities
  const { data: fetchedEntitiesData, isLoading: isFetching } = useGetEntities(
    payload,
    {
      enabled: hasReadEntitiesPermissions && missingIds.length > 0,
    },
  );

  // Combine cached and fetched entities
  const allEntities = useMemo(() => {
    const result: ShortEntityResponse[] = [];

    // Add entities from opensearch cache
    cachedEntities.forEach((entity) => {
      result.push({
        id: entity.id,
        external_id: entity.external_id || '',
        name_readable: getDisplayName(entity),
        risk_score: null,
        created_at: null,
        internal_status: null,
        status: entity.status || '',
        internal_entity_type: entity.type || '',
        entity_type: entity.type || null,
        type: entity.type || '',
        formatted_data: {},
      });
    });

    // Add entities from useGetEntities
    if (fetchedEntitiesData?.entities) {
      const existingKeys = new Set(
        result.map((e) =>
          valueField === ValueField.EXTERNAL_ID ? e.external_id : String(e.id),
        ),
      );

      fetchedEntitiesData.entities.forEach((entity) => {
        // Only add if not already in result (avoid duplicates)
        const key =
          valueField === ValueField.EXTERNAL_ID
            ? entity.external_id
            : String(entity.id);

        if (key && !existingKeys.has(key)) {
          result.push(entity);
          existingKeys.add(key);
        }
      });
    }

    return result;
  }, [cachedEntities, fetchedEntitiesData, valueField]);

  return {
    entities: allEntities,
    isLoading: isFetching,
  };
};
